import { type DataObject, type DataObjectList } from './DataObject'
import type { SurveyChoice } from './Survey'
import type { ReportVersionRef } from './Report'
import { type SlotStatus } from './DistributionPlan'

export enum DivisionCategory {
  University = 'University',
  College = 'College',
  School = 'School',
  Department = 'Department',
  Course = 'Course',
  Section = 'Section'
}

export interface DivisionResponse {
  defaultId: string
  divisionHierarchy: Division[]
  default_id?: string
}

export interface IAttribute {
  title: string
  value: string
  isInherited?: boolean
}

export interface ChildCategory {
  childCategoryId: string
  childCategoryName: string
}

export interface CategoryData {
  abbreviation: string | null
  archivedOn: string | null
  categoryId: string
  categoryName: string
  childrenCategory: ChildCategory | null
  childrenCount: number
  createdBy: string
  createdByUsername: string | null
  createdOn: any
  depth: number
  description: string | null
  divisionId: string
  divisionRelationship: string[]
  identifier: string | null
  isActive: boolean
  isArchived: boolean
  modifiedBy: string | null
  modifiedByUsername: string | null
  modifiedOn: string | null
  parent: string | null
  title: string
  versions: unknown[]
}

export interface Division {
  data: CategoryData
  categoryId?: string
  createdOn: string
  modifiedOn: string
  divisionId: string
  createdBy: string
  createdByUsername: string
  modifiedBy: string
  modifiedByUsername: string
  ownedBy: string
  categoryName: DivisionCategory
  title: string
  descr: string
  integrationSourceId: string | null
  institutionId: string | null
  children: Division[] | null
  abbreviation: string
  parent: {
    divisionId: string
    title: string
  }
  attributes?: IAttribute[]
  childrenCategory: {
    childCategoryId: string
    childCategoryName: string
  }
}
export interface InstructorDetails {
  title: string
  instructorId: string
  instructorName: string
  id: string
  isInScope?: boolean
}

export interface SurveyCompletionRate extends Omit<Division, 'children' | 'divisionId'> {
  /**
   * Sandbox mode on the owning Survey Project (CR-341). Absent → treated as false.
   *
   * Restored by the full-cycle spec (approved 2026-09-21): a sandboxed term now has a completion
   * rate, and the report header needs to say the figure belongs to a rehearsal.
   */
  isSandbox?: boolean

  completionCount: number
  completionPercent: number
  studentCount: number
  children: SurveyCompletionRate[]
  divisionId?: string
  sectionId?: string
  instructorDetails: InstructorDetails[]
  teachingAssistants?: string
  termId?: string
  crosslistedAbbrevs?: string | null
  surveyScore?: number | null
  isAnonymousSurvey?: boolean
  // Target + version metadata (report soft-delete versioning spec), emitted per completion-rate
  // row. `termName`/`surveyProjectTitle` name the action target; version fields drive the
  // revert/restore affordance. All optional/nullable: `previousVersion`/`latestVersion` stay
  // null until the backend enables report archiving, so the revert UI stays dormant.
  termName?: string
  surveyProjectTitle?: string
  version?: number | null
  previousVersion?: ReportVersionRef | null
  latestVersion?: ReportVersionRef | null
  modifiedByUsername: string
  // Inherited from Division (record-edit metadata) for Division-category rows. For
  // Section-category rows under this feature, the Backend overloads the same wire key
  // (`modified_on`) to instead mean "when this section's report data was last computed"
  // (CR-271 Behavior Change #9) — nothing in this codebase reads either meaning for
  // Section rows today, so this is safe, but don't assume it means record-edit time here.
  modifiedOn: string
  // CR-271: raw SlotNode status for section rows (Draft/Published/Live/Closed). Currently
  // present on children-list rows and optional on single-object completion-rate responses.
  slotStatus?: SlotStatus
}

export interface SurveyChoiceCounts extends SurveyChoice {
  count: number
}

export type IDivisionRow = Division

export type IDivisionList = DataObject<Division>

export type IDivisionRowDataType = IDivisionList[]

export type IDivision = DataObjectList<Division>
